import { useMemo } from "react";
import type { DemandAnalysis } from "../../api/types";
import {
  type Dir,
  fmtPct,
  fmtRiders,
  heatCell,
  heatStops,
  stationLabel,
} from "./demandMath";

interface Props {
  demand: DemandAnalysis;
  /** Riders per day between each pair of stops, within the selected time window. */
  matrix: number[][];
  dirNames: Record<Dir, string>;
  windowLabel: string;
}

/**
 * The origin–destination matrix as a heat table.
 *
 * Stops run in route order on both axes, so everything above the diagonal is
 * travelling one way and everything below it the other. Each triangle takes
 * its direction's hue — the same two hues as the charts beside it — and the
 * depth of the cell is the size of the flow. The figure is printed on every
 * cell, so the colour is a guide to the eye and never the only way to read a
 * value.
 */
export default function OdMatrix({ demand, matrix, dirNames, windowLabel }: Props) {
  const stops = demand.stops;
  const n = stops.length;

  const { max, rowTotals, colTotals, total } = useMemo(() => {
    let max = 0;
    const rowTotals = new Array<number>(n).fill(0);
    const colTotals = new Array<number>(n).fill(0);
    let total = 0;
    for (let o = 0; o < n; o++) {
      for (let d = 0; d < n; d++) {
        if (o === d) continue;
        const v = matrix[o]?.[d] ?? 0;
        max = Math.max(max, v);
        rowTotals[o] += v;
        colTotals[d] += v;
        total += v;
      }
    }
    return { max, rowTotals, colTotals, total };
  }, [matrix, n]);

  /** Share of a pair's all-day riders whose both ends were read, not inferred. */
  const observedShare = (o: number, d: number): number | null => {
    const all = demand.matrix[o]?.[d] ?? 0;
    if (all <= 0) return null;
    return Math.min(1, (demand.matrix_observed[o]?.[d] ?? 0) / all);
  };

  const anyEstimated = stops.some((_, o) =>
    stops.some((__, d) => {
      const share = observedShare(o, d);
      return o !== d && share != null && share < 0.5;
    })
  );

  return (
    <div className="dm-chart">
      <div className="dm-chart-head">
        <div>
          <h4>Where riders travel</h4>
          <p className="dm-chart-sub">
            Riders per day from the stop on the left to the stop above · {windowLabel}
          </p>
        </div>
      </div>

      <div className="dm-table-scroll">
        {/* Capped so a two-stop route does not stretch two cells across the card. */}
        <table className="dm-matrix" style={{ maxWidth: 230 + n * 92 }}>
          <thead>
            <tr>
              <th className="dm-matrix-corner" scope="col">
                <span className="dm-matrix-corner-to">To</span>
                <span className="dm-matrix-corner-from">From</span>
              </th>
              {stops.map((s) => (
                <th key={s.stop_id} scope="col" title={s.name}>{stationLabel(s.name)}</th>
              ))}
              <th scope="col" className="dm-matrix-total">Boardings</th>
            </tr>
          </thead>
          <tbody>
            {stops.map((from, o) => (
              <tr key={from.stop_id}>
                <th scope="row" title={from.name}>{stationLabel(from.name)}</th>
                {stops.map((to, d) => {
                  if (o === d) return <td key={to.stop_id} className="dm-matrix-diag" aria-hidden />;
                  const v = matrix[o]?.[d] ?? 0;
                  const dir: Dir = d > o ? "upward" : "downward";
                  const cell = heatCell(v, max, dir);
                  const share = observedShare(o, d);
                  const estimated = share != null && share < 0.5;
                  const title =
                    `${stationLabel(from.name)} to ${stationLabel(to.name)}: ${fmtRiders(v)} riders a day` +
                    (share == null
                      ? ""
                      : estimated
                        ? ` · mostly estimated (${fmtPct(share)} read from gates and tickets)`
                        : ` · ${fmtPct(share)} read from gates and tickets`);
                  return (
                    <td key={to.stop_id}
                      className={`dm-cell${cell?.light ? " dm-cell-light" : ""}${v > 0 ? "" : " dm-cell-empty"}`}
                      style={cell ? { background: cell.fill } : undefined}
                      title={title}>
                      {v > 0 ? (
                        <>
                          {estimated && <span className="dm-est" aria-label="estimated ">~</span>}
                          {fmtRiders(v)}
                        </>
                      ) : "–"}
                    </td>
                  );
                })}
                <td className="dm-matrix-total">{fmtRiders(rowTotals[o])}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">Alightings</th>
              {stops.map((s, d) => (
                <td key={s.stop_id} className="dm-matrix-total">{fmtRiders(colTotals[d])}</td>
              ))}
              <td className="dm-matrix-total dm-matrix-grand">{fmtRiders(total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="dm-legend dm-legend-matrix">
        {(["upward", "downward"] as Dir[]).map((dir) => (
          <span className="dm-legend-item" key={dir}>
            <span className="dm-ramp" aria-hidden>
              {heatStops(max, dir).map((s) => (
                <span key={s.fill} style={{ background: s.fill }} />
              ))}
            </span>
            {dirNames[dir]} <span className="dm-legend-note">({dir === "upward" ? "above" : "below"} the diagonal)</span>
          </span>
        ))}
        <span className="dm-legend-item dm-legend-note">
          Deeper is more riders, up to {fmtRiders(max)} a day.
        </span>
        {anyEstimated && (
          <span className="dm-legend-item dm-legend-note">
            <span className="dm-est">~</span> mostly estimated: few or no exits are recorded at the destination.
          </span>
        )}
      </div>
    </div>
  );
}
